import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';

const empty = {
  name: '', company_name: '', email: '', phone: '', designation: '',
  company_size: '', hiring_volume: '', city: '', message: '',
};

const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#9333EA]';

export default function EnterpriseModal({ open, onClose }) {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState({});
  const [genericErr, setGenericErr] = useState('');

    useEffect(() => {
    if (!open) return;
    let u = null;
    try { u = JSON.parse(localStorage.getItem('user')); } catch (e) { u = null; }

    const fill = (d, override = false) => {
      if (!d) return;
      const pick = (cur, val) => (override ? (val || cur) : (cur || val));
      setF((p) => ({
        ...p,
        name: pick(p.name, d.name || ''),
        email: pick(p.email, d.email || ''),
        phone: pick(p.phone, d.phone || d.mobile || ''),
        company_name: pick(p.company_name, d.company_name || d.company?.name || d.name || ''),
        company_size: pick(p.company_size, d.company_size || ''),
        city: pick(p.city, d.headquarters || d.company_headquarters || d.company_hq || d.city || ''),
      }));
    };

    fill(u);
    api.get('/profile').then((r) => fill(r.data, true)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr({});
    setGenericErr('');
    try {
      await api.post('/employer/enterprise-inquiry', { ...f, name: f.name || f.company_name });
      setDone(true);
      setF(empty);
    } catch (x) {
      const errors = x.response?.data?.errors;
      if (errors) setErr(errors);
      else setGenericErr(x.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, opts = {}) => (
    <label className="block mb-3">
      <span className="text-sm font-semibold text-gray-700">{label}{opts.req ? ' *' : ''}</span>
      <input
        type={opts.type || 'text'}
        value={f[k]}
        onChange={set(k)}
        required={opts.req}
        className={inputCls}
      />
      {err[k] && <span className="text-xs text-red-600">{err[k][0]}</span>}
    </label>
  );

  const close = () => { setDone(false); onClose(); };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold bg-gradient-to-r from-[#9333EA] to-[#EC4899] bg-clip-text text-transparent">
            Enterprise Enquiry
          </h3>
          <button type="button" onClick={close} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        {done ? (
          <div className="text-center py-8">
            <p className="text-lg font-semibold">Thank you! 🎉</p>
            <p className="text-gray-600 mt-1">Our team will contact you shortly.</p>
            <button
              type="button"
              onClick={close}
              className="mt-5 px-6 py-2 rounded-xl bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-bold"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            
            {field('company_name', 'Company Name', { req: true })}
            {field('email', 'Work Email', { req: true, type: 'email' })}
            {field('phone', 'Phone / WhatsApp', { req: true })}
           
            <label className="block mb-3">
              <span className="text-sm font-semibold text-gray-700">Company Size</span>
              <select value={f.company_size} onChange={set('company_size')} className={inputCls}>
                <option value="">Select</option>
                {f.company_size && !['1-50', '51-200', '201-1000', '1000+'].includes(f.company_size) && (
                  <option value={f.company_size}>{f.company_size}</option>
                )}
                <option>1-50</option>
                <option>51-200</option>
                <option>201-1000</option>
                <option>1000+</option>
              </select>
            </label>
            {field('hiring_volume', 'Jobs to post per month')}
            {field('city', 'City')}
            <label className="block mb-4">
              <span className="text-sm font-semibold text-gray-700">Message *</span>
              <textarea rows={4} required placeholder="Tell us about your hiring needs" value={f.message} onChange={set('message')} className={inputCls} />
            </label>
            {genericErr && <p className="text-sm text-red-600 mb-3">{genericErr}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-bold disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Submit'}
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
